import React, { useEffect, useRef, useState } from 'react';
import { songData } from '../data/songs';
import { extractVideoId } from '../utils/youtubeUtils';
import { partyModeGifs } from '../data/gifs';
import AnimatedGif from './AnimatedGif';

interface PartyModeVideoProps {
  isActive: boolean;
  showGifs?: boolean;
  onClose?: () => void;
  onToggleGifs?: () => void;
}

const PartyModeVideo: React.FC<PartyModeVideoProps> = ({ isActive, showGifs = false, onClose, onToggleGifs }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(0);
  const [allSongsWithVideos, setAllSongsWithVideos] = useState<any[]>([]);
  const [titleClickCount, setTitleClickCount] = useState<number>(0);

  // Debug logging
  console.log('PartyModeVideo rendered with props:', { isActive, showGifs, onClose: !!onClose, onToggleGifs: !!onToggleGifs });
  
  if (isActive) {
    console.log('Party mode is ACTIVE - buttons should be visible');
  }

  // Handle title clicks for GIF toggle (every 5 clicks)
  const handleTitleClick = () => {
    const newCount = titleClickCount + 1;
    setTitleClickCount(newCount);
    
    if (newCount % 5 === 0) {
      onToggleGifs?.();
    }
  };


  // Initialize videos list and select a random video when party mode is first activated
  useEffect(() => {
    if (isActive && allSongsWithVideos.length === 0) {
      const songsWithVideos = songData.filter(song => song.videoUrl);
      setAllSongsWithVideos(songsWithVideos);
      if (songsWithVideos.length > 0) {
        const randomIndex = Math.floor(Math.random() * songsWithVideos.length);
        setCurrentVideoIndex(randomIndex);
        setSelectedVideo(songsWithVideos[randomIndex]);
      }
    }
  }, [isActive, allSongsWithVideos.length]);

  // Update selected video when currentVideoIndex changes
  useEffect(() => {
    if (allSongsWithVideos.length > 0 && currentVideoIndex >= 0 && currentVideoIndex < allSongsWithVideos.length) {
      setSelectedVideo(allSongsWithVideos[currentVideoIndex]);
    }
  }, [currentVideoIndex, allSongsWithVideos]);

  const videoId = selectedVideo ? extractVideoId(selectedVideo.videoUrl!) : '';

  // Navigation functions
  const goToPreviousVideo = () => {
    if (allSongsWithVideos.length > 0) {
      const newIndex = currentVideoIndex === 0 ? allSongsWithVideos.length - 1 : currentVideoIndex - 1;
      setCurrentVideoIndex(newIndex);
    }
  };

  const goToNextVideo = () => {
    if (allSongsWithVideos.length > 0) {
      const newIndex = currentVideoIndex === allSongsWithVideos.length - 1 ? 0 : currentVideoIndex + 1;
      setCurrentVideoIndex(newIndex);
    }
  };

  // Force autoplay by recreating the iframe when party mode activates
  useEffect(() => {
    if (isActive && iframeRef.current && videoId) {
      // Create new iframe with autoplay parameters
      const iframe = iframeRef.current;
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&loop=1&playlist=${videoId}&controls=1&showinfo=0&rel=0&modestbranding=1`;
    }
  }, [isActive, videoId]);

  if (!isActive) {
    return null;
  }

  // Always show buttons for debugging
  console.log('Rendering party mode with buttons');

  // Show loading state while video is being selected
  if (!videoId) {
    return (
      <div className="party-mode-video-overlay">
        {/* Control buttons */}
        <div className="party-mode-controls">
          <button className="party-mode-close-btn" onClick={onClose || (() => console.log('Close clicked'))} title="Close party mode">
            ✕
          </button>
        </div>
        
        <div className="party-mode-logo">
          <img
            src="/LS_Logo_Text_Only_wht.png"
            alt="Lost Signal"
            className="party-mode-logo-image"
          />
        </div>
        <div className="party-mode-video-container">
          <div className="party-mode-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading party mode...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="party-mode-video-overlay">
      {/* Control buttons */}
      <div className="party-mode-controls">
        <button className="party-mode-close-btn" onClick={onClose || (() => console.log('Close clicked'))} title="Close party mode">
          ✕
        </button>
      </div>
      
      <div className="party-mode-logo">
        <img
          src="/LS_Logo_Text_Only_wht.png"
          alt="Lost Signal"
          className="party-mode-logo-image"
        />
      </div>
      <div className="party-mode-video-container">
        <iframe
          ref={iframeRef}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&loop=1&playlist=${videoId}&controls=1&showinfo=0&rel=0&modestbranding=1`}
          title={selectedVideo?.title || 'Party Mode Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="party-mode-iframe"
        />
        <div className="party-mode-video-title-container">
          <button 
            className="party-mode-nav-btn party-mode-nav-prev" 
            onClick={goToPreviousVideo}
            title="Previous video"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <div 
            className="party-mode-video-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {selectedVideo?.title} - {selectedVideo?.artist}
          </div>
          <button 
            className="party-mode-nav-btn party-mode-nav-next" 
            onClick={goToNextVideo}
            title="Next video"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
        <div className="party-mode-video-subtitle">
          Press escape to exit
        </div>
      </div>

      {/* Dancing dog GIFs in each corner - only show if showGifs is true */}
      {showGifs && (
        <>
          <div className="party-mode-corner-gifs">
            <AnimatedGif 
              gifUrls={partyModeGifs}
              className="party-mode-gif-container party-mode-gif-top-left"
              alt="Dancing dog"
              instanceId="top-left"
            />
            <AnimatedGif 
              gifUrls={partyModeGifs}
              className="party-mode-gif-container party-mode-gif-top-right"
              alt="Dancing dog"
              instanceId="top-right"
            />
            <AnimatedGif 
              gifUrls={partyModeGifs}
              className="party-mode-gif-container party-mode-gif-bottom-left"
              alt="Dancing dog"
              instanceId="bottom-left"
            />
            <AnimatedGif 
              gifUrls={partyModeGifs}
              className="party-mode-gif-container party-mode-gif-bottom-right"
              alt="Dancing dog"
              instanceId="bottom-right"
            />
          </div>
          
          {/* Side GIFs */}
          <div className="party-mode-side-gifs">
            <AnimatedGif 
              gifUrls={partyModeGifs}
              className="party-mode-gif-container party-mode-gif-left"
              alt="Dancing dog"
              instanceId="left"
            />
            <AnimatedGif 
              gifUrls={partyModeGifs}
              className="party-mode-gif-container party-mode-gif-right"
              alt="Dancing dog"
              instanceId="right"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PartyModeVideo;